// Meta (Facebook) Pixel helpers.
// The base pixel snippet lives in index.html and defines window.fbq.
// These wrappers keep call sites typed and no-op safely if the pixel
// hasn't loaded (e.g. blocked by an ad blocker or during SSR/tests).

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

const TRACKING_KEY = 'meta_pixel_tracking';

interface MetaTracking {
  fbclid?: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
}

/**
 * Capture fbclid + UTM params from the current URL and stash them in
 * sessionStorage so they survive SPA redirects (e.g. /order → /).
 * Call this BEFORE navigate() wipes the query string.
 */
export function captureMetaTracking(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const tracking: MetaTracking = {};
  const fbclid = params.get('fbclid');
  if (fbclid) tracking.fbclid = fbclid;
  for (const key of ['utm_source', 'utm_campaign', 'utm_content', 'utm_medium'] as const) {
    const val = params.get(key);
    if (val) tracking[key] = val;
  }
  if (Object.keys(tracking).length === 0) return;
  try {
    sessionStorage.setItem(TRACKING_KEY, JSON.stringify(tracking));
  } catch { /* quota / private mode */ }
}

function getMetaTracking(): MetaTracking {
  try {
    const raw = sessionStorage.getItem(TRACKING_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/** Fire a PageView — used on SPA route changes (initial load is in index.html). */
export function trackPixelPageView(): void {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'PageView');
  }
}

/** Fire a Lead event after a booking/order is successfully created. */
export function trackPixelLead(params?: Record<string, unknown>): void {
  if (typeof window !== 'undefined' && window.fbq) {
    const tracking = getMetaTracking();
    const eventID = `lead_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    window.fbq('track', 'Lead', {
      content_name: 'BookingRequest',
      content_category: 'Designer Booking',
      ...tracking,
      ...params,
    }, { eventID });
  }
}
